import {
    Button,
    Flex,
    SimpleGrid,
    Spinner,
    Text,
    useDisclosure
} from '@chakra-ui/react'
import { ReactNode, useEffect, useState } from 'react'
import colors from '../../../config/theme/colors'
import Zone from '../../../models/zone'
import { useGet } from '../../../hooks/useHttp'
import SelectZones from '../SelectZones'
import TabZones from '../TabZones'
import useJoinTables from '../hooks/useJoinTables'
import ModalContainer from '../libs/ModalContainer'
import TableItem from '../libs/TableItem'
import useExtendsJoinTables from '../../../hooks/useJoinTables'
import { TableData } from '../types'

interface ModalJoinTablesProps {
    element: (onOpen: () => void) => ReactNode
    zones: Zone[]
    onDone: (zoneId: number) => void
}

export default function ModalJoinTables({
    element,
    zones,
    onDone
}: ModalJoinTablesProps) {
    const { isOpen, onOpen, onClose } = useDisclosure()
    const [zoneId, setZoneId] = useState(() => zones[0].id)
    const use = useJoinTables(() => {
        onDone(zoneId)
        onClose()
    })
    const {
        data,
        reload,
        isLoading: tablesIsLoading
    } = useGet<TableData[]>(
        `3>/api/tables?sort=name&is_enabled=true&zone_id=${zoneId}&is_free=true`,
        []
    )

    useEffect(() => {
        if (isOpen) {
            reload()
        } else {
            use.setTableIds([])
        }
    }, [zoneId, isOpen])

    useEffect(() => {
        use.setTableIds([])
    }, [zoneId])

    function clickTableHandler(tableId: number) {
        if (use.tableIds.find(id => tableId === id)) {
            use.setTableIds(use.tableIds.filter(id => id !== tableId))
            return
        }
        use.setTableIds([...use.tableIds, tableId])
    }

    const tables = useExtendsJoinTables(data.result)

    return (
        <>
            {element(onOpen)}
            <ModalContainer
                title="Unir Mesas"
                isOpen={isOpen}
                onClose={onClose}
            >
                <SelectZones
                    zones={zones}
                    currentZone={zoneId}
                    onClick={setZoneId}
                />
                <TabZones
                    zones={zones}
                    currentZone={zoneId}
                    onClick={setZoneId}
                />
                <SimpleGrid
                    mt={4}
                    spacing={3}
                    minChildWidth={{
                        base: '120px',
                        xl: '150px'
                    }}
                    maxH={{
                        base: 'sm',
                        lg: 'container.sm'
                    }}
                    overflowY="auto"
                >
                    {!tablesIsLoading &&
                        tables.map(table => (
                            <TableItem
                                key={table.id}
                                table={table}
                                isActive={use.tableIds.includes(table.id)}
                                onClick={() => clickTableHandler(table.id)}
                            />
                        ))}
                </SimpleGrid>
                {tables.length === 0 && !tablesIsLoading && (
                    <Text textAlign="center">No hay mesas disponibles</Text>
                )}
                <Flex justify="center">
                    {tablesIsLoading && (
                        <Spinner mt={6} borderColor="gray.400" size="lg" />
                    )}
                </Flex>
                <Flex mt="8" justifyContent="flex-end">
                    <Button
                        variant="outline"
                        w="36"
                        onClick={() => onClose()}
                        disabled={use.isLoading}
                        mr={2}
                    >
                        Cancelar
                    </Button>
                    <Button
                        w="36"
                        bg={colors['alo-blue']}
                        color="white"
                        onClick={() => use.submit()}
                        isDisabled={use.tableIds.length < 2}
                        isLoading={use.isLoading}
                    >
                        Confirmar
                    </Button>
                </Flex>
            </ModalContainer>
        </>
    )
}
